/* ==========================================================================
   NON Somm

   One controller for every somm surface: the homepage hero, the product page
   chat, and the shop filter's prose line.

   ── API contract ─────────────────────────────────────────────────────────
   The endpoint is set in Theme settings → NON Somm. It is expected to be a
   Cloudflare Worker (hosted separately, NOT part of this theme).

     POST <endpoint>
     Content-Type: application/json

     Request  { "query":   "roast chicken, friday night",
                "context": "home" | "product" | "collection",
                "page":    "/products/non3",
                "code":    "NON3",            // product pages only
                "history": [{ "role": "user" | "assistant", "text": "…" }] }

     Response { "answer": "Roast chicken wants grip…",
                "picks":  ["NON3", "NON1"] }   // codes, resolved to real
                                               // products by the catalogue

   Streaming: if the response is text/event-stream, each `data:` frame is
   treated as a token and appended. Otherwise the JSON body is typed out
   locally at settings.sommTypeSpeed.

   With no endpoint configured the controller falls back to the canned seed
   answers carried over from the design, so the section is never dead.
   ========================================================================== */
(function () {
  'use strict';

  var NON = window.NON || {};
  var settings = NON.settings || {};
  var ENDPOINT = settings.sommEndpoint || '';

  /* --- catalogue: code → live Shopify product ---------------------------- */

  var catalogue = {};
  document.querySelectorAll('[data-non-catalogue]').forEach(function (node) {
    try {
      Object.assign(catalogue, JSON.parse(node.textContent));
    } catch (e) {
      /* A malformed catalogue must not take the somm down — but it must not
         pass unnoticed either. Without it every pick card resolves to nothing
         and the panel renders empty, which looks like the somm failing to
         answer rather than a data fault. */
      console.warn('[NON somm] catalogue JSON failed to parse — pick cards will not render. Fix [data-non-catalogue] in the section rendering this page.', e);
    }
  });
  if (!Object.keys(catalogue).length) {
    console.warn('[NON somm] no product catalogue found on this page. The somm will answer, but it cannot show which bottle it means. Every surface that renders [data-non-somm] must also render [data-non-catalogue].');
  }

  /* The codes this page can actually render a card for, sent with every
     question so the Worker recommends within them rather than within the range.

     The range and the catalogue have never been the same thing, and on one
     store nobody notices: non.world sells all six, so every pick resolves. A
     second store with a different catalogue turns that gap into a customer
     being told to drink NON7 and shown nothing — the warning in pickCard()
     below already names this exact cause, and until now it was the only place
     it was noticed.

     Derived from the catalogue rather than from a separate setting on purpose:
     the thing the Worker needs to know is precisely "what can be shown here",
     which is what this object already is. A second list would be a second
     thing to keep in step.

     Empty-string keys are dropped — `non-code` returns nothing for a product
     with no `custom.non_code`, and an empty key is not a bottle. */
  function availableCodes() {
    return Object.keys(catalogue)
      .map(function (c) { return String(c).trim().toUpperCase(); })
      .filter(Boolean);
  }

  function pickCard(code) {
    var p = catalogue[String(code).toUpperCase()];
    if (!p) {
      /* Returning '' here is what made the picks panel look broken: the somm
         names a bottle in prose and then shows no card, with nothing anywhere
         saying why. Usually the product is absent from THIS MARKET's catalog,
         which is exactly how the Not drinks row lost two products. */
      console.warn('[NON somm] "' + code + '" is not in this page\'s catalogue, so no card can be shown. Most often the product is not published to the current market.');
      return '';
    }
    /* Shopify titles carry the code ("NON3 Toasted Cinnamon & Yuzu"), and the
       card prints the code on its own line — so strip it from the name or the
       card reads "NON3NON3 Toasted…". */
    var pickName = String(p.title || '').replace(new RegExp('^\\s*' + code + '\\b[\\s—–-]*', 'i'), '') || p.title;
    return (
      '<a class="non-somm__pick" href="' + p.url + '">' +
      (p.image ? '<img src="' + p.image + '" alt="" width="120" height="120" decoding="async">' : '') +
      '<span class="non-somm__pick-meta">' +
      '<span class="non-somm__pick-code">' + code + '</span>' +
      '<span class="non-somm__pick-name">' + pickName + '</span>' +
      '<span class="non-somm__pick-note">' +
      (p.note ? p.note + ' &middot; ' : '') + p.price +
      (p.available === false ? ' &middot; ' + esc(t('soldOut', 'Sold out')) : '') +
      '</span></span></a>'
    );
  }

  /* --- the shoppable recommendation card --------------------------------- */

  /* The mobile card, and ONLY the mobile card.
   *
   * Desktop keeps `pickCard` above, unchanged, because the brief's one
   * non-negotiable is that the desktop storefront does not move. The two
   * differ in what they are for: the desktop row sits under an answer someone
   * is already reading on a wide page, while this is the end of a conversation
   * on a phone and has to be able to finish the job — price, availability and
   * a real Add, without leaving the sheet.
   *
   * EVERY FIELD COMES FROM THE LIQUID CATALOGUE. The Worker answers in bottle
   * codes and nothing else; the price, the variant, the availability and the
   * image are all resolved here from Shopify's own data rendered into the
   * page. That is the whole reason the Somm cannot invent a price: it is never
   * given one to repeat. */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* DECODE FIRST, THEN ESCAPE.
   *
   * The product metafields hold HTML entities, not characters: NON3's profile
   * is stored as "Bright, tart &amp; balanced". The desktop pick card writes
   * it straight into innerHTML, so the browser decodes it and a customer reads
   * "tart & balanced". Escaping it — which the recommendation card must do,
   * because it also writes innerHTML — turned the ampersand into &amp;amp; and
   * put the literal text "tart &amp; balanced" on the card.
   *
   * Decoding through a textarea and re-escaping fixes the display without
   * weakening anything: a genuine "<script>" in the data survives decode
   * unchanged and is escaped on the way out, and an encoded "&lt;script&gt;"
   * decodes to "<script>" and is escaped straight back. Either way it lands as
   * text. The textarea is never in the document and its content is never
   * parsed as markup — assigning to .innerHTML on a detached textarea only
   * resolves character references, it does not build elements.
   */
  var decoder = document.createElement('textarea');
  function txt(s) {
    if (s == null || s === '') return '';
    decoder.innerHTML = String(s);
    return esc(decoder.value);
  }

  function t(key, fallback) {
    return (NON.strings && NON.strings[key]) || fallback;
  }

  /* The Worker is asked for plain prose, but models occasionally return light
     Markdown anyway. These surfaces deliberately render text rather than HTML,
     so remove only presentation markers and preserve the wording and line
     breaks. This keeps recipe results editorial instead of showing "**Dish". */
  function cleanAnswer(value) {
    return String(value || '')
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/^\s*[-*]\s+/gm, '• ')
      .trim();
  }

  function recommendationCard(code, index) {
    var p = catalogue[String(code).toUpperCase()];
    if (!p) {
      console.warn('[NON somm] "' + code + '" is not in this page\'s catalogue, so no card can be shown. Most often the product is not published to the current market.');
      return '';
    }

    /* SOLD OUT IS A STATE, NOT A HIDDEN CARD. Dropping an unavailable bottle
       would leave the prose naming a wine with nothing under it — the exact
       silent gap that made the picks panel look broken before. It is shown,
       it is honest about being unavailable, and the Add is replaced rather
       than left there to fail. */
    var buyable = p.available !== false && p.variantId;

    var action = buyable
      ? '<button type="button" class="non-rec__add" data-non-somm-add ' +
        'data-variant-id="' + esc(p.variantId) + '" ' +
        'data-code="' + esc(code) + '">' +
        esc(t('sommRecAdd', 'Add')) + ' &mdash; ' + txt(p.price) +
        '</button>'
      : '<span class="non-rec__add non-rec__add--out">' + esc(t('soldOut', 'Sold out')) + '</span>';

    return (
      '<article class="non-rec" data-non-rec data-code="' + esc(code) + '">' +
      (p.image
        ? '<a class="non-rec__media" href="' + esc(p.url) + '" data-non-somm-view data-code="' + esc(code) + '" tabindex="-1" aria-hidden="true">' +
          '<img src="' + esc(p.image) + '" alt="" loading="lazy" width="120" height="120">' +
          '</a>'
        : '') +
      '<div class="non-rec__body">' +
      '<div class="non-mono non-rec__code">' + esc(code) + '</div>' +
      '<h3 class="non-rec__name">' +
      '<a href="' + esc(p.url) + '" data-non-somm-view data-code="' + esc(code) + '">' + txt(p.title) + '</a>' +
      '</h3>' +
      (p.note ? '<p class="non-rec__note">' + txt(p.note) + '</p>' : '') +
      '<div class="non-rec__actions">' +
      action +
      '<a class="non-rec__see" href="' + esc(p.url) + '" data-non-somm-view data-code="' + esc(code) + '">' +
      esc(t('sommRecSee', 'See bottle')) + '</a>' +
      '</div>' +
      '<div class="non-rec__more">' +
      '<button type="button" class="non-rec__ask" data-non-somm-why data-code="' + esc(code) + '">' +
      esc(t('sommRecWhy', 'Why this one?')) + '</button>' +
      '<button type="button" class="non-rec__ask" data-non-somm-another data-code="' + esc(code) + '">' +
      esc(t('sommRecAnother', 'Show me another')) + '</button>' +
      '</div>' +
      '<p class="non-rec__msg" data-non-rec-msg role="status" aria-live="polite" hidden></p>' +
      '</div>' +
      '</article>'
    );
  }

  /* --- fallback seeds ---------------------------------------------------- */

  /* READ AT CALL TIME, from the section's JSON, not captured at construction.
   *
   * Two reasons it had to change. The hero's inline form is gone, so there are
   * no seed BUTTONS left in the page to read — the suggestions ship as data.
   * And it was captured once when the controller was built: the sheet's chips
   * are created when it opens, long after that, so a fallback answer would
   * have been matched against an empty list and the offline path would have
   * had nothing to offer.
   *
   * Falls back to any seed buttons still in the root, which is what the
   * pairing page's dish chips are. */
  function seedsFor(root) {
    var fromJson = (NON.sommSeeds ? NON.sommSeeds() : []).map(function (s) {
      return { label: s.label, answer: s.answer || '', picks: s.picks || [] };
    });
    var fromDom = Array.prototype.map.call(root.querySelectorAll('[data-non-somm-seed], [data-non-pair-suggestion]'), function (btn) {
      return {
        label: btn.textContent.trim(),
        answer: btn.getAttribute('data-answer') || '',
        picks: (btn.getAttribute('data-picks') || '')
          .split(',')
          .map(function (s) { return s.trim(); })
          .filter(Boolean)
      };
    });
    return fromJson.concat(fromDom);
  }

  // Word-overlap match, same shape as the design's seed lookup.
  function matchSeed(seeds, query) {
    var q = query.trim().toLowerCase();
    if (!q) return null;
    return (
      seeds.find(function (s) {
        return s.label
          .toLowerCase()
          .split(/[ ,'?]+/)
          .some(function (w) { return w.length > 3 && q.indexOf(w) !== -1; });
      }) || null
    );
  }

  /* --- controller -------------------------------------------------------- */

  // Parsed per request rather than once at load: the theme editor can replace a
  // section's markup without a page reload, and a stale closure would then be
  // describing a bottle that is no longer on screen.
  function readFacts() {
    var el = document.querySelector('[data-non-somm-facts]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function Somm(form) {
    var root = form.closest('[data-non-somm-root]') || document;
    var input = form.querySelector('[data-non-somm-input]');
    var answerBox = root.querySelector('[data-non-somm-answer]');
    var stream = root.querySelector('[data-non-somm-stream]');
    var picksBox = root.querySelector('[data-non-somm-picks]');
    var submit = form.querySelector('[type="submit"]');
    var context = form.getAttribute('data-somm-context') || 'home';
    var queryPrefix = form.getAttribute('data-somm-query-prefix') || '';
    var code = form.getAttribute('data-somm-code') || '';
    var history = [];
    var timer = null;
    var lastQuery = '';
    var slowTimer = null;
    var requestTimer = null;
    var requestController = null;
    var submitLabel = submit ? submit.textContent : '';

    function setSubmitting(on) {
      if (!submit) return;
      submit.disabled = on;
      submit.textContent = on ? 'Thinking…' : submitLabel;
      form.toggleAttribute('aria-busy', on);
    }

    function show() {
      if (answerBox) answerBox.hidden = false;
      // The stream too, not just the box around it. On the product page they
      // are DIFFERENT elements: the transcript paragraph sits in
      // .non-sommbox__body (main-product.liquid:235) carrying a `hidden`
      // attribute, while [data-non-somm-answer] is a separate div further down
      // holding the picks. Unhiding only the box meant every answer was written
      // into a still-hidden paragraph — the request succeeded, the somm
      // replied, and the page showed nothing at all. On the hero the two are
      // nested, so this is a no-op there.
      if (stream) stream.hidden = false;

      // The pairing page's quiz has its own empty-state line ("Choose an
      // option to begin...") that only the quiz's own answer logic hides.
      // Typing a question through this free-text path instead left that line
      // sitting there next to the somm's answer, since this controller had no
      // idea the quiz's placeholder text even existed. Elements only exist on
      // the pairing page, so this is a no-op everywhere else.
      var quizWaiting = root.querySelector('[data-nonpair-waiting]');
      if (quizWaiting) quizWaiting.hidden = true;
      var quizResult = root.querySelector('[data-nonpair-result]');
      if (quizResult) quizResult.hidden = true;

      /* AND BRING IT INTO VIEW INSIDE THE SHEET.
       *
       * Unhiding is not showing. In the sheet the transcript sits above the
       * prompt chips and the field, in its own scroll container — so a
       * customer who has just typed a question is looking at the field, with a
       * keyboard under it, while the answer paints off the top of the panel.
       * The request succeeded, the somm replied, and from where they are
       * sitting nothing happened. Reported three times as "nothing came up"
       * and "didn't scroll to answer", and both are the same thing.
       *
       * Scrolls the SHEET'S OWN container where there is one — scrolling the
       * document instead does nothing, because the sheet is fixed and the body
       * is locked while it is open. Falls back to the element on the surfaces
       * that answer inline. */
      revealAnswer();
    }

    function showQuestion(query) {
      if (!stream || !query) return;
      var host = stream.parentNode;
      if (!host) return;
      var echo = host.querySelector('[data-non-somm-question]');
      if (!echo) {
        echo = document.createElement('p');
        echo.className = 'non-somm__question';
        echo.setAttribute('data-non-somm-question', '');
        host.insertBefore(echo, stream);
      }
      echo.textContent = 'You asked: ' + query;
      echo.hidden = false;
    }

    /* Deliberately not scrollIntoView() on the answer box in the sheet: the
       panel is a fixed, body-locked overlay, so the browser resolves that
       against the document and moves nothing anyone can see. The scroll
       container is the thing that has to move. */
    function revealAnswer() {
      var target = stream || answerBox;
      if (!target) return;
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var pane = target.closest('[data-non-sheet-scroll]');
      if (pane) {
        /* Computed, not scrollTo(0). The transcript happens to sit near the top
           of the panel today, but a conversation that has run for a few turns
           puts it anywhere, and "scroll to the top" would then show the wrong
           end of it. This lands the answer's top edge at the top of the pane
           wherever it actually is. */
        var top = pane.scrollTop + (target.getBoundingClientRect().top - pane.getBoundingClientRect().top);
        pane.scrollTo({ top: Math.max(0, top), behavior: still ? 'auto' : 'smooth' });
        return;
      }
      var r = target.getBoundingClientRect();
      var h = window.innerHeight || document.documentElement.clientHeight;
      if (r.top >= 0 && r.top < h - Math.min(160, r.height / 3)) return;
      target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    }

    /* The working state, per the NON Somm identity: the mark is the full stop
       lifted out of "NON.", locked bottom-left and never centred — "the offset
       is the whole idea". Thinking is that full stop multiplied, three dots
       reading left to right, which is also the states row in the identity doc.

       It is built here rather than in Liquid so the hero and the product page
       get the same mark without either template knowing about it. */
    var thinkingEl = null;
    var thinkingSince = 0;

    // A response can land in 150ms. Showing the mark and pulling it straight
    // back reads as a glitch, not as thought — so once it is up it stays up
    // for a beat, and the answer waits for it rather than the other way round.
    var MIN_THINK = 550;

    function endThinking(then) {
      var elapsed = Date.now() - thinkingSince;
      var wait = Math.max(0, MIN_THINK - elapsed);
      setTimeout(function () { thinking(false); if (then) then(); }, wait);
    }

    function thinking(on, failed) {
      if (!stream) return;

      if (!on) {
        // The reset belongs BEFORE the return, and did not use to be.
        // `if (!on) thinkingSince = 0` sat after this early return, so it was
        // unreachable: thinkingSince never went back to zero once the dots had
        // been shown. That is harmless while the answer arrives as JSON, and
        // fatal once it streams, because the paint below is guarded on
        // `!thinkingSince` — streamed text accumulated and was never written
        // to the panel. Pick cards rendered, the words never did.
        thinkingSince = 0;
        // The slow-response note belongs to this wait and nothing else. Left
        // armed it would fire onto the NEXT question's dots four seconds early.
        clearTimeout(slowTimer);
        if (thinkingEl) { thinkingEl.remove(); thinkingEl = null; }
        return;
      }

      if (!thinkingSince) thinkingSince = Date.now();

      if (!thinkingEl) {
        thinkingEl = document.createElement('div');
        thinkingEl.className = 'non-think';
        thinkingEl.setAttribute('role', 'status');
        // Screen readers get words; the dots are decoration to them.
        thinkingEl.setAttribute('aria-label', 'The somm is thinking');
        thinkingEl.innerHTML =
          '<span class="non-think__tile" aria-hidden="true">' +
          '<i class="non-think__dot"></i>' +
          '<i class="non-think__dot"></i>' +
          '<i class="non-think__dot"></i>' +
          '</span>';
        stream.parentNode.insertBefore(thinkingEl, stream);
      }

      thinkingEl.classList.toggle('is-failed', !!failed);
    }

    function type(text, picks) {
      clearInterval(timer);
      show();
      text = cleanAnswer(text);

      // Every target is optional. Each surface wires up a different subset —
      // the hero has stream + picks, the product page splits the two across
      // separate elements, the pairing page had picks and no stream at all —
      // and an unguarded write to a missing one throws mid-handler. A thrown
      // click is indistinguishable from a dead button, which is exactly how the
      // pairing chips presented: correct markup, correct styling, nothing
      // happening. Falling back to plain text is the right failure: the answer
      // still arrives, it just does not animate.
      if (picksBox) {
        picksBox.hidden = true;
        picksBox.innerHTML = '';
      }
      if (!stream) return renderPicks(picks);

      if (!settings.sommStream) {
        stream.textContent = text;
        return renderPicks(picks);
      }

      var i = 0;
      var speed = settings.sommTypeSpeed || 12;
      stream.innerHTML = '';
      var cursor = document.createElement('span');
      cursor.className = 'non-somm__cursor';

      timer = setInterval(function () {
        i += 3;
        if (i >= text.length) {
          clearInterval(timer);
          stream.textContent = text;
          renderPicks(picks);
        } else {
          stream.textContent = text.slice(0, i);
          stream.appendChild(cursor);
        }
      }, speed);
    }

    function renderPicks(picks) {
      if (window.NON && NON.answered) {
        /* Reported here rather than on fetch success, because an answer the
           reader never sees is not an answer. This fires when it is on screen. */
        NON.answered('somm', { picks: (picks || []).length, has_box: !!picksBox });
      }

      /* The named-event dictionary the brief asks for, alongside the
         started/answered/failed triple that already exists. The two measure
         different things and both are wanted: the triple answers "is the
         feature alive", these answer "did it sell anything". */
      if (NON.sommTrack) {
        NON.sommTrack('somm_recommendation_returned', {
          surface: (form.__nonSommSurface || {}).surface || context,
          count: (picks || []).length,
          recommended_product_id: (picks || [])[0] || '',
          recommendation_id: (picks || []).join('+')
        });
      }

      if (!picksBox || !picks || !picks.length) return;

      /* Which card. The sheet gets the shoppable one; every other surface
         keeps the row it has today. Decided from where the panel IS rather
         than from a viewport query, so a desktop-width theme editor preview of
         the sheet still renders the sheet's card. */
      var inSheet = !!picksBox.closest('[data-non-sheet]');
      var html = picks
        .map(function (code, i) { return inSheet ? recommendationCard(code, i) : pickCard(code); })
        .filter(Boolean)
        .join('');
      if (!html) {
        var missing = String(picks[0] || '').toUpperCase();
        picksBox.innerHTML =
          '<div class="non-rec-fail" role="status">' +
          '<p>' + esc(missing ? missing + ' is not available in this store right now.' : 'That bottle is not available right now.') + '</p>' +
          '<div class="non-rec-fail__actions">' +
          '<button type="button" class="non-rec__ask" data-non-somm-another data-code="' + esc(missing) + '">' +
          esc(t('sommRecAnother', 'Show me another')) + '</button>' +
          '</div></div>';
        picksBox.hidden = false;
        return;
      }
      picksBox.innerHTML = html;
      picksBox.hidden = false;
    }


    /* ------------------------------------------------------- ask a human */

    /* Some questions deserve a person: an allergy the ingredient list cannot
       clear, a wholesale enquiry, a journalist. The Worker marks those and
       returns a drafted email; this renders it.

       NOTHING IS SENT WITHOUT THE CUSTOMER PRESSING SEND. The draft is a
       starting point in an editable field, not an outbox — a message sent in
       someone's name that they never read is worse than no feature. The email
       field is theirs so a reply can reach them.

       It posts through Shopify's own contact form, the same endpoint the
       contact page uses, so it lands in the store's contact inbox with no new
       credential, no third-party sender and no deliverability setup of its
       own. */
    function renderEscalation(esc) {
      if (!esc) return;
      /* The card is Shopify's own contact form, rendered by ask-form.liquid
         and sitting in the DOM from page load. This only fills it in and shows
         it — it does not build a form and it never submits one.

         Everything else was tried: a hand-built payload to /contact (400), the
         same inside a hidden iframe (400), and a scripted .submit() on the real
         form, which Shopify answered with a page titled "Missing CAPTCHA
         token". The token is issued when a human interacts with a form Shopify
         rendered. So the human does. */
      var card = document.querySelector('[data-non-ask-card]');
      if (!card) return;
      /* A card already showing a sent or failed state is the result of a
         submit the customer just made. Asking another question must not
         overwrite the confirmation they came back to read. */
      if (card.querySelector('.non-ask__msg--sent, .non-ask__msg--bad')) return;

      var body = card.querySelector('[data-non-ask-message]');
      var subject = card.querySelector('[data-non-ask-subject]');
      var name = card.querySelector('[data-non-ask-name]');

      // Never overwrite something the customer has already started editing.
      // An answer arriving while they type would otherwise wipe it.
      if (body && !body.value.trim()) body.value = esc.body || '';
      if (subject) subject.value = esc.subject || 'Question from the NON site';
      if (name) name.value = 'NON Somm' + (esc.about ? ' \u2014 ' + esc.about : '');

      card.hidden = false;
    }

    /* THE SERVICE IS DOWN, AND SAYS SO.
     *
     * The canned fallback below is a good answer when a seed genuinely matches
     * what was asked. When none does, it used to serve seeds[0] — so someone
     * asking about allergens got the roast chicken answer, confidently, with
     * nothing anywhere saying the Somm never saw the question. The file's own
     * comment noticed: "a worker that is down looks exactly like one that is
     * up."
     *
     * On the sheet that becomes this panel instead: it says the Somm is away,
     * it does not block shopping, and it offers the two things worth offering.
     * Desktop keeps the old path untouched — changing how the desktop Somm
     * behaves is one of the brief's explicit regressions.
     */
    function failurePanel(query) {
      if (!picksBox) return false;
      show();
      if (stream) stream.textContent = t('sommFailHead', 'The Somm is away from the table.');
      picksBox.innerHTML =
        '<div class="non-rec-fail">' +
        '<p class="non-rec-fail__b">' + esc(t('sommFailBody', 'Browse the range or try again.')) + '</p>' +
        '<div class="non-rec-fail__actions">' +
        '<button type="button" class="non-rec__add" data-non-somm-retry>' +
        esc(t('sommRetry', 'Try again')) + '</button>' +
        '<a class="non-rec__see" href="/collections/the-range">' +
        esc(t('sommShopRange', 'Shop the range')) + '</a>' +
        '</div></div>';
      picksBox.hidden = false;
      lastQuery = query;
      return true;
    }

    function fallback(query) {
      var seeds = seedsFor(root);
      var hit = matchSeed(seeds, query);

      /* In the sheet, an unmatched question during an outage is a failure, not
         an excuse to read out an unrelated canned answer. */
      if (!hit && picksBox && picksBox.closest('[data-non-sheet]')) {
        thinking(false);
        failurePanel(query);
        return;
      }

      var first = seeds[0];
      var answer = hit ? hit.answer : (first ? first.answer : NON.strings.sommError);
      var picks = hit ? hit.picks : (first ? first.picks : []);

      // Even a canned answer gets the beat. An answer that appears the instant
      // you ask reads as a lookup; the same answer after a moment reads as a
      // somm thinking about it. The wait is honest either way — it is not
      // pretending to compute, it is pacing a conversation.
      show();
      if (stream) stream.textContent = '';
      thinking(true);

      endThinking(function () { type(answer, picks); });
    }

    function ask(query, promptType) {
      if (!query.trim()) return;

      /* EVERY question, from every surface, passes through here — typed,
       * seeded, chipped, retried, or asked by a "Why this one?" button. So
       * this is where the question is counted.
       *
       * It was counted in the sheet's own ask() wrapper first, which only
       * covers questions the sheet initiates. A customer who tapped a
       * follow-up chip or typed into the field went through somm.js directly
       * and was never recorded, so somm_question_submitted would have
       * undercounted the two commonest paths while looking like it worked. */
      if (NON.sommTrack) {
        NON.sommTrack('somm_question_submitted', {
          surface: (form.__nonSommSurface || {}).surface || context,
          intent: (form.__nonSommSurface || {}).intent || '',
          meal_category: (form.__nonSommSurface || {}).meal_category || '',
          prompt_type: promptType || 'typed',
          /* The LENGTH, never the words. Capturing what a customer typed is a
             data-collection decision with a privacy-policy consequence, and
             the brief rules out sending raw conversation text. */
          chars: query.trim().length
        });
      }

      /* Length and surface only — never the question itself. Capturing what a
         customer typed is a data-collection decision with a privacy-policy
         consequence, and that is task #14. */
      if (window.NON && NON.started) {
        NON.started('somm', { chars: query.trim().length, context: context, has_endpoint: !!ENDPOINT });
      }

      history.push({ role: 'user', text: query });
      lastQuery = query;
      showQuestion(query);

      if (!ENDPOINT) return fallback(query);

      setSubmitting(true);
      show();
      if (stream) stream.textContent = '';
      thinking(true);

      /* A real deadline. The slow note explains a long request; it must not
         become an indefinite state. Abort at 20s and use the same customer-
         safe fallback as a network failure. */
      clearTimeout(requestTimer);
      requestController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      requestTimer = setTimeout(function () {
        if (requestController) requestController.abort();
      }, 20000);

      /* A WORD AFTER THE WAIT STOPS BEING SHORT.
       *
       * The dots are the right answer for a second or two and the wrong one at
       * eight — at that point an animation with no words is indistinguishable
       * from a hang, and the brief rules out an indefinite typing animation
       * for exactly that reason. This does not replace the mark; it puts a
       * sentence beside it so the wait is accounted for.
       *
       * 4.5s, because the Worker's own p95 is comfortably inside that and this
       * should be the exception rather than a caption everyone reads. */
      clearTimeout(slowTimer);
      slowTimer = setTimeout(function () {
        if (!thinkingEl) return;
        var note = thinkingEl.querySelector('.non-think__slow');
        if (note) return;
        note = document.createElement('span');
        note.className = 'non-think__slow';
        note.textContent = t('sommSlow', 'The Somm is still considering the table…');
        thinkingEl.appendChild(note);
      }, 4500);

      askOnce({
        method: 'POST',
        signal: requestController ? requestController.signal : undefined,
        headers: {
          'Content-Type': 'application/json',
          // Ask for the stream. The Worker answers with SSE on the two
          // single-call paths and plain JSON everywhere else; the handler
          // below already switches on the response content-type, so both
          // shapes are handled and neither is assumed.
          //
          // Sent as a header rather than a body flag so the Worker can keep
          // serving JSON to every other caller — the audit harnesses and
          // /somm/suggestions read res.json(), and a Worker that streamed at
          // everyone would break all of them to save a second here.
          Accept: 'text/event-stream, application/json'
        },
        body: JSON.stringify({
          query: query,
          context: context,
          page: window.location.pathname,
          code: code,
          // The Somm's answers are generated, so they cannot be translated
          // after the fact — the language has to travel with the question.
          // theme.liquid already sets <html lang> from request.locale, so the
          // storefront's own locale is the source and nothing new is plumbed.
          // The Worker treats 'en' and anything it does not know as English.
          locale: settings.sommLocale || document.documentElement.lang || 'en',
          // The bottle's own spec sheet, when the page publishes one. The
          // request used to carry a code and nothing else, so anything not
          // derivable from the code — storage, serving, ingredients — had no
          // source and the Somm could only decline or guess. Sent as facts
          // rather than prose so the Worker can quote them rather than
          // paraphrase.
          facts: readFacts(),
          // What this store sells, as codes. See availableCodes().
          available: availableCodes(),
          // WHERE THE QUESTION CAME FROM.
          //
          // The Worker already knew WHAT was asked and, on a product page,
          // which bottle. It did not know that this question arrived by
          // tapping "Mains" on the homepage triptych — so a customer who had
          // already told the site they were cooking a main course had to say
          // it again in words, and the first answer was a general one.
          //
          // Additive: an object the Worker can ignore entirely and still
          // behave exactly as it does today. Nothing here is required for a
          // reply, and nothing here is free text the customer typed.
          surface: form.__nonSommSurface || null,
          history: history.slice(-8)
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('somm ' + res.status);

          var type_ = res.headers.get('content-type') || '';
          if (type_.indexOf('text/event-stream') !== -1) return readStream(res);

          return res.json().then(function (data) {
            if (!data || typeof data.answer !== 'string' || !data.answer.trim()) {
              throw new Error('invalid_response');
            }
            endThinking(function () {
              type(data.answer, Array.isArray(data.picks) ? data.picks : []);
              renderEscalation(data.escalate);
              history.push({ role: 'assistant', text: data.answer || '' });
            });
          });
        })
        .catch(function (err) {
          // Endpoint down or CORS-blocked: fall back rather than show nothing.
          // The mark goes red for a beat first — the identity doc has a red
          // state and this is what it is for — then the fallback answers.
          //
          // Reported, because the fallback is convincing: the reader still
          // gets an answer, so a worker that is down looks exactly like one
          // that is up. Without this the somm could fail for days in silence.
          var reason = err && err.name === 'AbortError'
            ? 'timeout'
            : err && err.message === 'invalid_response'
              ? 'invalid_response'
              : 'endpoint_unreachable';
          if (window.NON && NON.failed) {
            NON.failed('somm', { reason: reason, context: context });
          }
          if (NON.sommTrack) {
            NON.sommTrack('somm_recommendation_failed', {
              surface: (form.__nonSommSurface || {}).surface || context,
              reason: reason
            });
          }
          console.warn('[NON somm] endpoint unreachable — serving the canned fallback. The customer still sees an answer, so this will not look broken.', err);
          thinking(true, true);
          setTimeout(function () { thinking(false); fallback(query); }, 700);
        })
        .finally(function () {
          clearTimeout(requestTimer);
          requestController = null;
          setSubmitting(false);
        });
    }

    // SSE: append tokens as they arrive; a trailing {"picks":[…]} frame sets picks.
    /* One retry, and only on a NETWORK failure.
     *
     * The 204-question mega-test had one request return `fetch failed` after
     * seventy seconds. There was no retry, so that customer got a dead box —
     * 0.5% of questions, but 100% of that person's experience.
     *
     * Deliberately narrow:
     *  - a rejected promise only. An HTTP response, including a 500, is the
     *    Worker answering, and its own fallback is better than asking twice.
     *  - once. A Worker that is down stays down, and a second failure should
     *    reach the canned fallback quickly rather than after three timeouts.
     *  - a short pause first, because the failure mode this exists for is a
     *    dropped connection rather than a busy server.
     */
    function askOnce(init) {
      return fetch(ENDPOINT, init).catch(function (err) {
        if (init.signal && init.signal.aborted) throw err;
        console.warn('[NON somm] request failed, retrying once', err);
        return new Promise(function (resolve) { setTimeout(resolve, 400); })
          .then(function () { return fetch(ENDPOINT, init); });
      });
    }

    function readStream(res) {
      var reader = res.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';
      var text = '';

      function pump() {
        return reader.read().then(function (chunk) {
          if (chunk.done) {
            // A stream shorter than MIN_THINK would otherwise finish with the
            // dots still up and nothing painted.
            if (!text.trim()) throw new Error('invalid_response');
            thinking(false);
            if (stream && text) stream.textContent = text;
            history.push({ role: 'assistant', text: text });
            return;
          }
          buffer += decoder.decode(chunk.value, { stream: true });
          var frames = buffer.split('\n\n');
          buffer = frames.pop();

          frames.forEach(function (frame) {
            var line = frame.replace(/^data:\s*/, '').trim();
            if (!line || line === '[DONE]') return;
            try {
              var parsed = JSON.parse(line);
              if (parsed.token) {
                text += parsed.token;
              } else {
                /* THE TAIL FRAME. Everything that is not a token is the final
                   payload, and it is handled as one thing rather than as a
                   picks frame that happens to be first.

                   It used to `return` on picks, which would have silently
                   dropped an escalation riding the same frame — and the
                   escalation always rides the same frame.

                   Its `answer` is also authoritative over the tokens we
                   accumulated. The Worker suppresses the stream from the
                   first '[[' so the ask-a-human marker cannot type itself
                   across the screen, which means on an escalated answer the
                   token stream is deliberately short. Repainting from the
                   tail is what completes the sentence. */
                if (parsed.answer) {
                  text = parsed.answer;
                  if (stream) stream.textContent = text;
                }
                if (parsed.picks) renderPicks(parsed.picks);
                if (parsed.escalate) renderEscalation(parsed.escalate);
                return;
              }
            } catch (e) {
              text += line;
            }
            // The dots hand over on the FIRST token, not at the end of the
            // stream. Leaving them up while text arrives underneath would say
            // "still thinking" while it is plainly already answering.
            // Streaming hands over on the first token, but not before the
            // minimum — otherwise a fast first token defeats the whole point.
            if (text && thinkingSince && Date.now() - thinkingSince >= MIN_THINK) thinking(false);
            if (!thinkingSince && stream) stream.textContent = text;
          });

          return pump();
        });
      }

      show();
      return pump();
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitter = e.submitter || document.activeElement;
      var suggestion = submitter && submitter.matches && submitter.matches('[data-non-pair-suggestion]')
        ? submitter.textContent.trim()
        : '';
      var query = suggestion || (input ? input.value.trim() : '');
      if (!query) return;

      /* Clearing immediately is the visible confirmation that Enter worked.
         The question remains in history and lastQuery for follow-ups. */
      if (input) {
        input.value = '';
        input.placeholder = 'Ask another question';
      }
      ask(queryPrefix + query, suggestion ? 'suggestion' : 'typed');
    });

    /* The hero's "Ask the Somm" button. It scrolls the field into view and
       focuses it rather than navigating anywhere — the Somm is on this page,
       and a button that reloads the page to reach something six inches down
       would be theatre.

       Bound here rather than in the section so it shares the controller that
       already knows which input belongs to which surface; the product page has
       its own, and a document-wide selector would focus the wrong one. */
    /* --- the recommendation card's own controls -------------------------- */

    /* Delegated on the root for the same reason the seeds are: these buttons
     * are written into the panel by renderPicks long after this runs, and they
     * are replaced wholesale on every new answer. */
    if (!root.__nonSommRec) {
      root.__nonSommRec = true;

      root.addEventListener('click', function (e) {
        var surface = (form.__nonSommSurface || {}).surface || context;

        /* ADD TO CART.
         *
         * Goes through NON.cart.add, which is Shopify's own AJAX API, and
         * therefore reports success only when Shopify has said so. The button
         * is disabled for the whole round trip, so a double tap cannot post
         * twice, and the label never claims "Added" on the strength of the
         * click alone — the brief is explicit that nothing may claim an item
         * was added until Shopify confirms it. */
        var add = e.target.closest('[data-non-somm-add]');
        if (add) {
          e.preventDefault();
          if (add.disabled) return;
          var variant = add.getAttribute('data-variant-id');
          var code = add.getAttribute('data-code') || '';
          if (!variant || !NON.cart) return;

          var card = add.closest('[data-non-rec]');
          var msg = card && card.querySelector('[data-non-rec-msg]');
          var label = add.textContent;

          add.disabled = true;
          add.textContent = t('sommRecAdding', 'Adding…');
          if (msg) { msg.hidden = true; msg.textContent = ''; }

          NON.cart
            .add(variant, 1)
            .then(function (cart) {
              add.textContent = t('sommRecAdded', 'Added');
              /* Only here. Everything before this point is an intention. */
              if (NON.sommTrack) {
                NON.sommTrack('somm_add_to_cart', {
                  surface: surface,
                  recommended_product_id: code,
                  recommended_variant_id: variant,
                  currency: (cart && cart.currency) || '',
                  position: card ? Array.prototype.indexOf.call(card.parentNode.children, card) : 0
                });
              }
            })
            .catch(function (err) {
              add.disabled = false;
              add.textContent = label;
              if (msg) {
                msg.textContent = (err && err.message) || t('sommRecAddFailed', 'That didn’t add. Try again.');
                msg.hidden = false;
              }
              if (NON.sommTrack) {
                NON.sommTrack('somm_add_to_cart_failed', {
                  surface: surface,
                  recommended_product_id: code,
                  recommended_variant_id: variant,
                  reason: (err && err.message) || 'unknown'
                });
              }
            });
          return;
        }

        /* Following a card through to the bottle. Recorded on the way out so
           recommendation-to-PDP can be measured against recommendation-to-cart
           — the two rates the brief asks to compare. */
        var view = e.target.closest('[data-non-somm-view]');
        if (view) {
          if (NON.sommTrack) {
            NON.sommTrack('somm_product_viewed', {
              surface: surface,
              recommended_product_id: view.getAttribute('data-code') || ''
            });
          }
          return;
        }

        /* "Why this one?" and "Show me another" are questions, not controls.
           They go back through ask() so the answer arrives the same way every
           other answer does, and so the Worker keeps the thread. */
        var why = e.target.closest('[data-non-somm-why]');
        if (why) {
          e.preventDefault();
          /* ASK IT AGAINST THE DISH, not in the abstract.
           *
           * This sent "Why this one? NON3" — a question with no subject. The
           * Worker cannot explain a choice without knowing what it was chosen
           * FOR, so it routed the string as a general enquiry and answered
           * about the bottle rather than about the match: true, and not what
           * was asked.
           *
           * The dish is already here in lastQuery. Naming it turns the request
           * into the one the button is actually making — why THIS bottle for
           * THAT plate, and what it beat. Falls back to the old phrasing when
           * there is no previous question, which is the case for a
           * recommendation restored from a transcript. */
          var whyCode = why.getAttribute('data-code') || '';
          ask(
            lastQuery
              ? 'Why ' + whyCode + ' for "' + lastQuery + '" rather than the alternative? '
                + 'Explain the match — what in the dish it is answering.'
              : t('sommRecWhy', 'Why this one?') + ' ' + whyCode
          );
          return;
        }

        var another = e.target.closest('[data-non-somm-another]');
        if (another) {
          e.preventDefault();
          if (NON.sommTrack) {
            NON.sommTrack('somm_product_compared', {
              surface: surface,
              recommended_product_id: another.getAttribute('data-code') || ''
            });
          }
          var anotherCode = another.getAttribute('data-code') || '';
          ask(
            lastQuery
              ? 'Show me another bottle for "' + lastQuery + '" — not ' + anotherCode
              : t('sommRecAnother', 'Show me another') + ' — not ' + anotherCode
          );
          return;
        }

        /* Retry after a failure asks the SAME question again rather than
           clearing the box and waiting for it to be retyped. */
        var retry = e.target.closest('[data-non-somm-retry]');
        if (retry) {
          e.preventDefault();
          if (lastQuery) ask(lastQuery);
        }
      });
    }

    var focusBtn = root.querySelector('[data-non-somm-focus]');
    if (focusBtn && input) {
      focusBtn.addEventListener('click', function () {
        // Focus first, then scroll: focus() alone jumps abruptly on some
        // engines, and preventScroll lets the smooth scroll own the movement.
        input.focus({ preventScroll: true });
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    /* DELEGATED, not bound per button.
     *
     * Two reasons, and the second is the one that forced it. Binding each
     * button meant a chip added AFTER load was dead — and the mobile sheet
     * clones its chips out of this section at open time, so every one of them
     * would have been. It also meant a Shopify section reload re-bound every
     * surviving button a second time, which is the duplicate-listener problem
     * the brief calls out: one click, two requests, two analytics rows.
     *
     * A listener on the root has neither problem: it is installed once and it
     * sees buttons that did not exist when it was installed. */
    if (!root.__nonSommSeeds) {
      root.__nonSommSeeds = true;
      root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-non-somm-seed]');
        if (!btn || !root.contains(btn)) return;

        var label = btn.textContent.trim();
        /* A chip is a submitted question, not editable draft text. Clear the
           field immediately so the response state is unmistakable and the
           next question starts clean on every inline surface. */
        if (input) input.value = '';
        // Seeds answer from their own vetted copy rather than the model, but
        // they go through the same thinking beat — this path was the reason
        // the working state was never seen, since the chips are what most
        // people press first.
        var answer = btn.getAttribute('data-answer') || '';
        var picks = (btn.getAttribute('data-picks') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);

        // No canned answer means ASK FOR A REAL ONE. The pairing page's dish
        // chips set data-answer to the dish name itself, so this path typed the
        // label straight back at you — the click worked, the request never
        // happened, and it read as a dead button. A seed without vetted copy is
        // a question, not an answer.
        if (!answer) return ask(label);

        show();
        if (stream) stream.textContent = '';
        thinking(true);
        endThinking(function () { type(answer, picks); });
      });
    }

    /* The way in from outside this file. The mobile sheet's prompt chips, its
     * "Show me another", and the triptych's follow-up answers all need to put
     * a question to THIS controller — the one that owns the surface they are
     * displayed on — rather than to whichever one happens to be first in the
     * document. */
    form.__nonSommAsk = function (text, promptType) {
      if (input) input.value = text;
      ask(text, promptType);
    };

    form.__nonSommSetContext = function (data) {
      data = data || {};
      context = data.context || 'home';
      code = data.code || '';
      form.setAttribute('data-somm-context', context);
      form.setAttribute('data-somm-code', code);
      form.__nonSommSurface = {
        surface: context === 'product' ? 'product_page' : context,
        product_id: data.product || '',
        variant_id: data.variant || '',
        product_title: data.title || '',
        product_price: data.price || '',
        product_available: data.available || ''
      };
    };
  }

  /* One native controller and one transcript: only the global sheet owns a
     Somm instance. Homepage, Pairing, PDP and floating entries are launchers
     that pass context into this form through non-somm-global.js. */
  document.querySelectorAll('[data-non-somm]').forEach(Somm);
})();
